import React, { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, MapPin, Mail, ExternalLink, ChevronDown } from 'lucide-react';
import { FiretreeLogo } from './FiretreeLogo';
import { openContactModal } from './ContactModal';
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const links = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'How We Work',
    path: '/how-we-work'
  }, {
    name: 'How We Partner',
    path: '/our-process'
  }, {
    name: 'Our Partners',
    path: '/who-we-fund'
  }, {
    name: 'Engage With Us',
    path: '/engage'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  const isActive = (path: string) => location.pathname === path;
  const isChildActive = (children?: {
    path: string;
  }[]) => children ? children.some(child => location.pathname === child.path) : false;
  const handleMobileClick = useCallback((path: string, index: number) => {
    setClickedIndex(index);
    setTimeout(() => {
      if (path === '/contact') {
        openContactModal();
      } else {
        navigate(path);
      }
      setIsOpen(false);
      setClickedIndex(null);
    }, 400);
  }, [navigate]);
  return <>
      {/* Tier 1: Top Info Bar (Scrolls away, hidden on mobile) */}
      <div className="hidden lg:flex w-full bg-brand-cream border-b border-brand-shape/30 py-2.5 px-4 sm:px-6 lg:px-8" data-id="element-125">
        <div className="max-w-7xl mx-auto w-full flex justify-between items-center text-xs font-medium text-brand-brown" data-id="element-126">
          <div className="flex items-center space-x-6" data-id="element-127">
            <div className="flex items-center gap-1.5" data-id="element-128">
              <MapPin className="w-3.5 h-3.5" data-id="element-129" />
              <span data-id="element-130">Singapore</span>
            </div>
            <a href="mailto:contact@firetree.org" className="flex items-center gap-1.5 hover:text-brand-charcoal transition-colors" data-id="element-131">
              <Mail className="w-3.5 h-3.5" data-id="element-132" />
              <span data-id="element-133">contact@firetree.org</span>
            </a>
          </div>
          <div data-id="element-134">
            <a href="https://firetree.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-brand-charcoal transition-colors" data-id="element-135">
              <span data-id="element-136">firetree.org</span>
              <ExternalLink className="w-3.5 h-3.5" data-id="element-137" />
            </a>
          </div>
        </div>
      </div>

      {/* Tier 2: Main Navigation (Sticky) */}
      <nav className="sticky top-0 z-50 w-full bg-brand-cream/90 backdrop-blur-md border-b border-brand-shape/30" data-id="element-138">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-139">
          <div className="flex justify-between items-center h-20" data-id="element-140">
            {/* Logo - Original (no invert) */}
            <div className="flex-shrink-0 flex items-center relative z-50" data-id="element-141">
              <FiretreeLogo data-id="element-142" />
            </div>

            {/* Desktop Nav */}
            <div className="hidden lg:flex lg:items-center" data-id="element-143">
              {links.map((link, index) => {
              const active = isActive(link.path) || isChildActive(link.children);
              return <div key={link.name} className="flex items-center" data-id="element-144">
                    {index > 0 && <div className="w-px h-4 bg-brand-shape/60 mx-5" data-id="element-145" />}

                    {link.children ? <div className="relative group" data-id="element-146">
                        <Link to={link.path} className={`flex items-center gap-1 text-sm transition-colors hover:text-brand-red pb-1 ${active ? 'text-brand-red font-bold border-b-2 border-brand-red' : 'text-brand-charcoal font-medium border-b-2 border-transparent'}`} data-id="element-147">
                          {link.name}
                          <ChevronDown className="w-3.5 h-3.5" data-id="element-148" />
                        </Link>

                        {/* Dropdown */}
                        <div className="absolute top-full left-0 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50" data-id="element-149">
                          <div className="bg-white border border-brand-shape/50 rounded-lg shadow-lg py-2 min-w-[200px]" data-id="element-150">
                            {link.children.map(child => <Link key={child.name} to={child.path} className={`block px-4 py-2 text-sm hover:bg-brand-cream hover:text-brand-red transition-colors ${isActive(child.path) ? 'text-brand-red font-bold bg-brand-cream/50' : 'text-brand-charcoal'}`} data-id="element-151">
                                {child.name}
                              </Link>)}
                          </div>
                        </div>
                      </div> : link.path === '/contact' ? <>
                        <a href="https://firetree.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2 bg-brand-charcoal text-white hover:bg-brand-charcoal/80 transition-all duration-300 ml-2" data-id="element-152">
                          Firetree Group
                          <ExternalLink className="w-3.5 h-3.5" data-id="element-153" />
                        </a>
                        <button onClick={openContactModal} className="text-sm font-semibold px-5 py-2 bg-brand-red text-white hover:bg-brand-charcoal transition-all duration-300 ml-2" data-id="element-154">
                          {link.name}
                        </button>
                      </> : <Link to={link.path} className={`text-sm transition-colors hover:text-brand-red pb-1 ${active ? 'text-brand-red font-bold border-b-2 border-brand-red' : 'text-brand-charcoal font-medium border-b-2 border-transparent'}`} data-id="element-155">
                        {link.name}
                      </Link>}
                  </div>;
            })}
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center lg:hidden relative z-50" data-id="element-156">
              <button onClick={() => {
              setIsOpen(!isOpen);
              setClickedIndex(null);
            }} className="text-brand-charcoal hover:text-brand-red focus:outline-none transition-colors" aria-expanded={isOpen} data-id="element-157">
                <span className="sr-only" data-id="element-158">Open main menu</span>
                {isOpen ? <X className="h-7 w-7" data-id="element-159" /> : <Menu className="h-7 w-7" data-id="element-160" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Nav — Full Screen Overlay (outside nav to avoid backdrop-blur containing block) */}
      <div className={`lg:hidden fixed inset-0 bg-brand-cream z-[9999] flex flex-col transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} data-id="element-161">
        {/* Close button */}
        <div className="absolute top-6 right-6 z-10" data-id="element-162">
          <button onClick={() => {
          setIsOpen(false);
          setClickedIndex(null);
        }} className="text-brand-charcoal hover:text-brand-red focus:outline-none transition-colors" aria-label="Close menu" data-id="element-163">
            <X className="h-7 w-7" data-id="element-164" />
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-8 min-h-screen overflow-y-auto py-20" data-id="element-165">
          <div className="space-y-4 w-full max-w-sm" data-id="element-166">
            {links.map((link, index) => {
            const isClicked = clickedIndex === index;
            const hasClicked = clickedIndex !== null;
            return <div key={link.name} className={`transition-all duration-400 ease-out ${isClicked ? '-translate-y-6 opacity-0' : hasClicked ? 'opacity-30 translate-y-2' : isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`} style={{
              transitionDelay: isOpen && !hasClicked ? `${index * 50}ms` : '0ms'
            }} data-id="element-167">
                  <button onClick={() => handleMobileClick(link.path, index)} className={`block w-full text-center py-2 text-3xl font-serif ${isActive(link.path) ? 'text-brand-red' : 'text-brand-charcoal'}`} data-id="element-168">
                    {link.name}
                  </button>
                </div>;
          })}
          </div>
        </div>
      </div>
    </>;
}